// =============================================================================
// Stock dual-run glue (Phase 2) — the thin layer the screens call so a stock tap
// writes to BOTH the old Apps Script sheet (unchanged, still the source of truth)
// AND the new Supabase tables, side-by-side, until we've proven parity and flip
// reads to realtime (strangler cut-over — see supabase/README.md).
//
// The Supabase half is BEST-EFFORT by design: every recorder below swallows its
// own errors and NEVER throws into the live till path, so a Supabase hiccup can't
// stop staff trading. The Apps Script write stays exactly as it is today.
//
// Screens import ONE thing:  import * as stockSync from "../lib/stockSync.js"
//   stockSync.recordRequest({ siteKey, item })           // till -> kitchen request
//   stockSync.recordUndo({ siteKey, item })              // undo within the window
//   stockSync.recordReceived({ siteKey, item })          // marked received
//   stockSync.recordKitchen({ siteKey, item, action })   // kitchen flag/delivery
// and the login + read/realtime helpers are re-exported at the bottom.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import * as stock from "./stock.js";
import * as outbox from "./stockOutbox.js";
import { loginWithPin, currentPass, lock, ensureAnonSession } from "./auth.js";
import { record as recordActivity } from "./activity.js";
// stockReadEnabled is consulted ONLY inside the write-drop gate's function body
// (never at module-eval time), so the stockReader<->stockSync import cycle is
// safe: by the time any UI calls stockWriteDropEnabled() both modules are fully
// evaluated. The gate REFUSES to drop the Sheet write unless the till already
// reads Supabase for this host.
import { stockReadEnabled } from "./stockReader.js";

// --- dual-run gate: which live kiosks shadow to Supabase ---------------------
// Broadened 1 Jul 2026 from kitchen-only to ALL THREE production kiosks. The
// kitchen tablet turned out to be the lowest-signal surface (stock is tapped on
// the shop tills), so a kitchen-only trial captured nothing. Each host below now
// runs the best-effort Supabase shadow alongside the unchanged Apps Script write.
// Any OTHER host (local dev, the staging worker, an old *.netlify.app URL) stays
// COMPLETELY INERT — no Supabase calls, no login, no writes.
// `?dualrun=on` force-enables (e.g. a pre-shift test); `?dualrun=off` disables.
const DUAL_RUN_HOSTS = new Set([
  "trafford.eatrolled.com",
  "arndale.eatrolled.com",
  "mcr-kitchen.eatrolled.com",
  "meadowhall.eatrolled.com",   // 16 Jul: start Meadowhall shadow-writes so its sell-outs accumulate in Supabase; its READ can flip only after it dual-runs + reconciles clean (0 events today).
]);
export function dualRunEnabled() {
  if (typeof window === "undefined") return true; // Node test harness: allowed
  try {
    const q = new URLSearchParams(window.location.search).get("dualrun");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return DUAL_RUN_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// =============================================================================
// THE STOCK WRITE-DROP GATE (decommission step 4 — the riskiest piece).
// When ON for a host, the Supabase write becomes the AWAITED record and the Apps
// Script Sheet POST is SKIPPED — that host's stock taps stop touching Google.
//
// SHIPS EMPTY. A go-live is a reviewed one-line host add, and ONLY after: the two
// Command Centre reads are flipped, section 2 of the write-drop evidence query is
// clean on prod, and (for the End-of-Day coupling) the Apps Script column-12
// patch is pasted. See STOCK_WRITE_DROP_STATUS.md.
//
// THREE code-enforced preconditions, ALL required (never trust the host list alone):
//   1. the host is listed (or ?stockwritedrop=on) — the reviewed go-live switch;
//   2. stockReadEnabled() — the till must ALREADY read Supabase, else dropping the
//      Sheet write blinds its own list;
//   3. dualRunEnabled() — the durable outbox must be armed for this host.
// TWO more go-live preconditions the CODE cannot check — the reviewer MUST confirm
// before adding a host (documented in STOCK_WRITE_DROP_STATUS.md):
//   4. the reorder-email cutover (RUNBOOK_REORDER_EMAIL_CUTOVER.md) — a FOH
//      running-low/out-of-stock tap's reorder email fires SERVER-SIDE in Apps Script
//      when the Sheet POST lands; drop that POST and the email stops unless the
//      Supabase reorder signal already drives it (kitchen already raises it; FOH
//      raises it once JJ's PR #262 lands — see DECISIONS_NEEDED BE-1);
//   5. the End-of-Day column-12 Apps Script patch is pasted (Wave 3) so the EOD
//      snapshot does not freeze when stock stops reaching the Sheet.
// `?stockwritedrop=off` ALWAYS wins (an instant per-tab kill). If a host is listed
// but a precondition is missing, the gate stays FALSE and says why ONCE — it never
// silently half-drops. arndale is CLOSED (16 Aug) and must never be listed.
const STOCK_WRITE_DROP_HOSTS = new Set([
  /* empty until go-live — a reviewed one-line add, one host at a time:
     "trafford.eatrolled.com", "meadowhall.eatrolled.com", "mcr-kitchen.eatrolled.com"
     — never arndale (closed 16 Aug 2026), never bare site keys. */
]);

// Parse the ?stockwritedrop override: "on" | "off" | null. Pure (search injectable
// for tests; falls back to window.location.search in the browser).
export function stockWriteDropOverride(search) {
  try {
    const s = search != null ? search : (typeof window !== "undefined" ? window.location.search : "");
    const q = new URLSearchParams(s).get("stockwritedrop");
    if (q === "on" || q === "off") return q;
  } catch (e) {}
  return null;
}

// The PURE decision — every input explicit, no globals, so the whole truth table
// (host in/out, override on/off, read-not-flipped, dual-run-off) is unit-tested
// with no window. `off` always wins; a listed/forced host still needs BOTH
// preconditions. Returns true only when the write may be dropped.
export function decideStockWriteDrop({ override = null, hostListed = false, readEnabled = false, dualRun = false } = {}) {
  if (override === "off") return false;              // instant kill — always wins
  const wanted = override === "on" || hostListed;   // the reviewed go-live switch
  if (!wanted) return false;
  if (!readEnabled) return false;                   // till must already read Supabase
  if (!dualRun) return false;                       // outbox must be armed
  return true;
}

// Runtime combiner the screens call. Reads the host + override + the two
// preconditions, and warns ONCE when a listed/forced host can't yet drop.
export function stockWriteDropEnabled(search) {
  let host = "";
  if (typeof window !== "undefined") host = (window.location.hostname || "").toLowerCase();
  const override = stockWriteDropOverride(search);
  const hostListed = STOCK_WRITE_DROP_HOSTS.has(host);
  const readEnabled = stockReadEnabled();
  const dualRun = dualRunEnabled();
  const on = decideStockWriteDrop({ override, hostListed, readEnabled, dualRun });
  if (!on && (override === "on" || hostListed)) {
    console.warn(
      "[stock-sync] write-drop REFUSED (staying dual-write): read-flip=" + readEnabled +
      " dual-run=" + dualRun + " — a host must already read Supabase AND dual-run before its Sheet write is dropped."
    );
  }
  return on;
}

// The app's action names -> stock_activity event shape. Keeps the screen edits
// tiny: the kitchen keeps firing 'stockRunningLow' etc., this maps it across.
const KITCHEN_EVENT = {
  stockRunningLow:        { event: "running_low",  flag: "running_low" },
  stockOutOfStock:        { event: "out_of_stock", flag: "out_of_stock" },
  backInStock:            { event: "back_in_stock" },
  stockOrdered:           { event: "ordered", ordered: true },
  stockDelivered:         { event: "delivered" },
  customDeliveryReceived: { event: "delivered" },
  stockWastage:           { event: "wastage" },
};

// --- site key ('tc') -> Supabase uuid (cached) -------------------------------
// The screens speak in site keys; Supabase rows key on the uuid. Resolve once
// and cache (memory + localStorage) so the first tap after a reload is instant.
const _siteIdCache = new Map();
const LS_SITE = "rolled.supabase.siteId::";

export async function siteIdForKey(siteKey) {
  if (!siteKey) return null;
  if (_siteIdCache.has(siteKey)) return _siteIdCache.get(siteKey);
  try {
    const cached = localStorage.getItem(LS_SITE + siteKey);
    if (cached) { _siteIdCache.set(siteKey, cached); return cached; }
  } catch (e) { /* no localStorage (e.g. SSR/tests) — fall through to query */ }
  const { data, error } = await supabase.from("sites").select("id").eq("key", siteKey).single();
  if (error || !data) return null;
  _siteIdCache.set(siteKey, data.id);
  try { localStorage.setItem(LS_SITE + siteKey, data.id); } catch (e) {}
  return data.id;
}

// --- best-effort wrapper: run a Supabase write, swallow + log, never throw ----
export async function shadow(label, fn) {
  try {
    const { data, error } = (await fn()) || {};
    if (error) { console.warn(`[stock-sync] ${label} — Supabase rejected:`, error.message || error); return { ok: false, error }; }
    return { ok: true, data };
  } catch (e) {
    console.warn(`[stock-sync] ${label} — Supabase threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// --- idempotency key ---------------------------------------------------------
// Stable per (site, action, item, day) so a double-tap or a retried write
// collapses to one row (the DB has a unique index on idem_key) instead of a
// duplicate. Distinct events on the same item/day differ by the action segment.
export function idem(...parts) {
  return parts.filter((p) => p != null && p !== "").join("|").slice(0, 200);
}

// --- per-action verify ---------------------------------------------------------
// Every action re-verifies the PIN that was just entered (Adam's 4 Jul decision:
// no unlock window — matches the till exactly, PIN per action). This is also what
// makes the who-did-it stamping truthful: the pass minted here carries THIS
// person's staff id, and the database copies it onto the row (migration 0006).
// Reusing an earlier pass would stamp the previous person's name.
// Best-effort — a failure here just means the shadow writes skip; the live
// Apps Script path is untouched.
// In-flight dedup: a synchronous BURST of taps with the same pin (a batch submit's
// forEach, or the kitchen's delivered+running-low pair) shares ONE login instead of N
// round-trips — while sequential taps still re-verify per action (Adam's no-unlock-window
// rule), because the entry is cleared as soon as the login settles.
const _inflightPass = new Map();
export async function ensurePass(pin, siteKey) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  if (!pin) return { ok: false, error: "no pin" };   // no pin -> no login round-trip; the write stays queued
  const key = pin + "|" + (siteKey || "");
  const existing = _inflightPass.get(key);
  if (existing) return existing;
  const p = (async () => {
    try {
      const res = await loginWithPin(pin, { siteKey });
      return res?.ok ? { ok: true } : { ok: false, error: res };
    } catch (e) { return { ok: false, error: e }; }
  })();
  _inflightPass.set(key, p);
  try { return await p; } finally { _inflightPass.delete(key); }
}

// --- activity log line ---------------------------------------------------------
// Every stock write is also a "who did what, where, when" event for the estate
// activity feed (activity.js → activity_record, 0042/0076). One line per action,
// FIRE-AND-FORGET: it is never awaited, never carries a PIN (the database resolves
// the staff id from the login pass already on the shared connection), and can
// never break a stock tap. It writes on `supabase` — the SAME connection the
// stock row was written and signed on, so the audit line carries the same person.
//
// The mapping is a pure, exported function so the wiring is unit-tested without a
// network round-trip; the write goes through the `_activitySink` seam for the
// same reason. `kind` is always 'action' — a stock tap is "somebody did a thing".
const STOCK_ACTIVITY_VERB = {
  request:                "stockRequested",
  undo:                   "stockRequestUndone",
  received:               "stockReceived",
  stockRunningLow:        "stockRunningLow",
  stockOutOfStock:        "stockOutOfStock",
  backInStock:            "stockBackInStock",
  stockOrdered:           "stockOrdered",
  stockDelivered:         "stockDelivered",
  customDeliveryReceived: "customDeliveryReceived",
  stockWastage:           "stockWastage",
};

export function stockActivityLine(action, item) {
  const verb = STOCK_ACTIVITY_VERB[action];
  if (!verb) return null;
  return { action: verb, kind: "action", target: item || null };
}

// Test seam: production points this at the real record(); a unit test swaps it to
// observe what would have been written without touching the network.
export const _activitySink = { record: recordActivity };

export function logStockActivity(siteKey, action, item) {
  const line = stockActivityLine(action, item);
  if (!line) return null;
  try {
    return _activitySink.record({ siteKey, ...line, client: supabase });
  } catch (e) {
    // record() is built never to throw; this is belt-and-suspenders so a future
    // change to it still can't be the reason a stock tap fails.
    console.warn("[stock-sync] activity line threw (stock write unaffected):", e?.message || e);
    return null;
  }
}

// --- durable outbox: land a stock tap even across a blip/unmount/sleep ---------
// Decommission step 2. Mirrors the proven sell-out outbox (selloutSync/selloutOutbox):
// the recorder takes the staffPin and, when the screen calls it directly at the tap:
//   (1) ENQUEUES the row SYNCHRONOUSLY — durable BEFORE the login round-trip, so a
//       slow login / unmount / tab-sleep can no longer lose it (the failure the
//       callsite-gated first cut reopened; adversarial review, 31 Aug);
//   (2) verifies the PIN and STAMPS the resolved staff_id onto the queued row, so a
//       flushed-later row attributes to the TAPPER, not whoever's session is warm at
//       flush time;
//   (3) drains (itself + any leftovers). A self-arming flusher re-drains on
//       online/pageshow/visibility. 23505 (the one-open index / stock_activity
//       idem_key) counts as landed.
// The Google Sheet write in the screen is untouched and STAYS the source of truth —
// this only hardens the Supabase shadow so it can eventually be authoritative without
// losing a request. Behind dualRunEnabled(); inert on any non-production host.
//
// ATTRIBUTION PRECONDITION: insertStockRow REFUSES an un-stamped row (keeps it queued
// for a genuine warm pass) rather than letting the flush-time JWT stamp a bystander.
// Still validate on a REAL TILL before the Sheet fallback is ever dropped (step 4).

// A per-tap nonce so a stock_requests op (request/undo/received — no server idem_key,
// deduped by the one-open partial index) is NEVER collapsed in the queue across a
// close: a legitimate same-day re-request after a delivery must keep its own entry.
let _tapSeq = 0;
function tapNonce() {
  _tapSeq += 1;
  try { return Date.now().toString(36) + "-" + _tapSeq; } catch (e) { return "n" + _tapSeq; }
}

// Land one queued stock row. Routes by entry.kind to the same stock.js seams the
// old shadow() used; resolves the site uuid at flush time; treats a unique-violation
// (23505 — the one-open index or the stock_activity idem_key) as SUCCESS. Never throws.
async function insertStockRow(entry) {
  // Refuse an UN-ATTRIBUTED row — keep it queued for a genuine warm pass, never land
  // it under whatever JWT is warm at flush time (that would mis-attribute a leftover
  // to a bystander). Mirrors selloutSync. The immediate drain runs right after the
  // recorder's ensurePass+stampStaff, so a normal tap is stamped and lands at once.
  if (!entry.staff_id) return { ok: false, error: "unattributed — kept for a warm pass" };
  const siteId = await siteIdForKey(entry.siteKey);
  if (!siteId) return { ok: false, error: "no site id for " + entry.siteKey };
  try {
    if (entry.kind === "request") {
      const { error } = await stock.createStockRequest({ siteId, itemName: entry.item, catalogId: entry.catalogId ?? null, type: entry.type || "standard", requestDate: entry.day, requestedBy: entry.staff_id });
      if (!error) return { ok: true };
      if (error.code === "23505") return { ok: true, duplicate: true }; // one-open index collapse
      return { ok: false, error };
    }
    if (entry.kind === "undo") {
      const { data, error } = await stock.undoStockRequest({ siteId, itemName: entry.item, requestDate: entry.day });
      if (error) return { ok: false, error };
      // 0 rows = nothing open to undo (already undone/received) OR an RLS-blocked write.
      // We only reach here WITH a warm pass (staff_id resolved), so an RLS block is not
      // the cause; treat it as landed (idempotent). A lost undo would only leave a
      // phantom OPEN request — the safe direction, and the nightly audit reconciles it.
      return { ok: true };
    }
    if (entry.kind === "received") {
      const { error } = await stock.markReceived({ siteId, itemName: entry.item, requestDate: entry.day, receivedBy: entry.staff_id });
      return error ? { ok: false, error } : { ok: true };
    }
    // kitchen events -> stock_activity (carries its own idem_key)
    const { data, error } = await stock.logKitchenEvent({
      siteId, itemName: entry.item, event: entry.event, flag: entry.flag ?? null, ordered: entry.ordered || false,
      supplierRef: entry.supplierRef ?? null, eta: entry.eta ?? null, note: entry.note ?? null,
      photoPath: entry.photoPath ?? null, qty: entry.qty ?? null, staffId: entry.staff_id,
      idemKey: entry.idem_key,
      // Lane BP: the device fix for the kitchen offsite flag (1300) — present only on
      // an authoritative (write-drop ON) entry; the row builder omits the columns when null.
      deviceLat: entry.deviceLat ?? null, deviceLng: entry.deviceLng ?? null, deviceAccuracyM: entry.deviceAccuracyM ?? null,
    });
    if (error) {
      if (error.code === "23505") return { ok: true, duplicate: true }; // idem collapse — already landed
      return { ok: false, error };
    }
    // A REAL delivery closes the open request row(s) — ONLY when a NEW delivered row
    // actually landed. An idem-collided re-submit returns no data: the original
    // delivery already closed the request. Custom inbound (customDeliveryReceived,
    // also 'delivered') was never on the running list, so only 'stockDelivered' closes.
    //
    // CLOSE CEILING = the TAP time (entry.tappedAt, captured durably at the tap), NOT
    // data.created_at (the server INSERT time). For a DELAYED flush, created_at is the
    // flush time — which can be hours after the delivery — and closing "everything open
    // at flush time" would sweep a legitimate re-request raised AFTER the delivery
    // closed (a live open need -> a stockout). Using the tap time restores the original
    // "requests open at the delivery moment" semantics even when the write lands later.
    if (entry.action === "stockDelivered" && data && data.created_at) {
      const closeCeiling = entry.tappedAt || data.created_at;
      try { await stock.closeOpenRequests({ siteId, itemName: entry.item, deliveredAt: closeCeiling, receivedBy: entry.staff_id }); }
      catch (e) { console.warn("[stock-sync] close-request threw:", e?.message || e); }
    }
    return { ok: true, data };
  } catch (e) {
    console.warn(`[stock-sync] insert ${entry.idem_key} threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// Test seam: production lands rows via insertStockRow; a unit test swaps this to
// observe the wiring with no network (same idea as _activitySink).
export const _stockSink = { insert: insertStockRow };

// Drain the durable outbox, firing each landed row's activity line on the SAME
// success path (immediate OR flushed-later).
export function flushStockOutbox() {
  return outbox.flush({
    insert: (entry) => _stockSink.insert(entry),
    onLanded: (entry) => { if (entry.activity) logStockActivity(entry.siteKey, entry.activity.action, entry.activity.item); },
  });
}

// Enqueue synchronously (durable), verify + stamp the pass, then drain. Best-effort;
// never throws into the till path. Returns { ok, queued, pass } — the pass is handed
// back so a caller (e.g. the kitchen reorder-signal) can reuse the SAME login instead
// of a second round-trip.
async function durableRecord(label, { staffPin, siteKey }, entry) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    outbox.enqueue(entry);                                 // (1) durable FIRST — before the login await
    const pass = await ensurePass(staffPin, siteKey);      // (2) per-action attribution (returned as-is for the caller)
    if (pass?.ok) {
      try { const cur = await currentPass(); if (cur?.staffId) outbox.stampStaff(entry.idem_key, cur.staffId); }
      catch (e) { /* stamp is best-effort; an un-stamped row stays queued (refused by insertStockRow) */ }
    } else {
      console.warn(`[stock-sync] ${label} — no warm pass; the write is queued durably and flushes on the next warm pass`);
    }
    const res = await flushStockOutbox();                  // (3) land it (+ any leftovers)
    return { ok: res.landed > 0, queued: outbox.size() > 0, pass };
  } catch (e) {
    console.warn(`[stock-sync] ${label} — durable record threw (write stays queued):`, e?.message || e);
    return { ok: false, error: e, queued: true };
  }
}

// Self-arming flusher: drain when connectivity/visibility returns, and once on load.
let _flusherArmed = false;
function armStockFlusher() {
  if (_flusherArmed || typeof window === "undefined") return;
  _flusherArmed = true;
  const drain = () => { try { flushStockOutbox(); } catch (e) {} };
  try {
    window.addEventListener("online", drain);
    window.addEventListener("pageshow", drain);
    if (typeof document !== "undefined")
      document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") drain(); });
  } catch (e) { /* non-DOM env */ }
  drain(); // land anything a previous session left behind
}
if (typeof window !== "undefined" && dualRunEnabled()) armStockFlusher();

// --- high-level recorders the screens call (now DURABLE) ----------------------
export async function recordRequest({ siteKey, item, staffPin = null, catalogId = null, type = "standard", day = stock.londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  return durableRecord(`request ${item}`, { staffPin, siteKey }, {
    kind: "request", siteKey, item, catalogId, type, day,
    idem_key: idem(siteKey, "req", item, day, tapNonce()),   // per-tap: never collapse a re-request
    activity: { action: "request", item },
  });
}

export async function recordUndo({ siteKey, item, staffPin = null, day = stock.londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  return durableRecord(`undo ${item}`, { staffPin, siteKey }, {
    kind: "undo", siteKey, item, day,
    idem_key: idem(siteKey, "undo", item, day, tapNonce()),
    activity: { action: "undo", item },
  });
}

export async function recordReceived({ siteKey, item, staffPin = null, day = stock.londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  return durableRecord(`received ${item}`, { staffPin, siteKey }, {
    kind: "received", siteKey, item, day,
    idem_key: idem(siteKey, "rcv", item, day, tapNonce()),
    activity: { action: "received", item },
  });
}

export async function recordKitchen({ siteKey, item, action, staffPin = null, supplierRef = null, eta = null, note = null, photoPath = null, qty = null, day = stock.londonToday() }) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  const map = KITCHEN_EVENT[action];
  if (!map) return { ok: false, error: "unmapped kitchen action " + action };
  return durableRecord(`${action} ${item}`, { staffPin, siteKey }, {
    kind: "kitchen", siteKey, item, action, day,
    event: map.event, flag: map.flag ?? null, ordered: map.ordered || false,
    supplierRef, eta, note, photoPath, qty,
    tappedAt: (() => { try { return new Date().toISOString(); } catch (e) { return null; } })(),  // durable tap time -> delivery close ceiling
    idem_key: idem(siteKey, action, item, day),   // existing kitchen idem_key = server dedup (once/item/day)
    activity: { action, item },
  });
}

// --- the AUTHORITATIVE write (the write-drop ON path) -------------------------
// When STOCK_WRITE_DROP_HOSTS is ON for a host, a stock tap no longer POSTs to the
// Sheet: the Supabase landing IS the record. That makes durability + an HONEST
// per-tap verdict non-negotiable. stockWriteAuthoritative():
//   (1) builds the SAME outbox entry the fire-and-forget recorder builds (via the
//       shared buildStockEntry — pinned equal to the recorder by a unit test), and
//       ENQUEUES it synchronously (durable BEFORE any await — a slow login / unmount
//       / sleep can't lose it);
//   (2) registers a per-entry landing waiter (outbox.awaitEntry) keyed on THIS
//       entry's idem_key BEFORE the flush — so a flush that lands some OTHER queued
//       row can never be mistaken for this write landing;
//   (3) verifies the PIN + stamps attribution (identical to durableRecord);
//   (4) drives the drain and resolves on THIS entry's OWN outcome: inserted / 23505
//       -> {ok:true}; sink error / no warm pass / never-settles-timeout -> {ok:false}.
// A failed landing STAYS queued for the self-arming flusher (nothing lost) AND is
// reported honestly NOW, so the call site can run today's rollback UI. It never
// throws into the till path.
//
// The four recorders above are left byte-for-byte UNCHANGED (they are the OFF/dual
// path); buildStockEntry duplicates their tiny entry shape and stockSync.test.mjs
// asserts the two stay identical, so there is no drift.

// Pure entry builder — MUST match the recorder for each kind (guarded by a test).
// Throws only for an unmapped kitchen action (mirrors recordKitchen's error return
// path — the caller catches and reports !ok).
export function buildStockEntry(kind, a = {}) {
  const siteKey = a.siteKey;
  const item = a.item;
  const day = a.day != null ? a.day : stock.londonToday();
  if (kind === "request") {
    return {
      kind: "request", siteKey, item,
      catalogId: a.catalogId ?? null, type: a.type || "standard", day,
      idem_key: idem(siteKey, "req", item, day, tapNonce()),
      activity: { action: "request", item },
    };
  }
  if (kind === "undo") {
    return {
      kind: "undo", siteKey, item, day,
      idem_key: idem(siteKey, "undo", item, day, tapNonce()),
      activity: { action: "undo", item },
    };
  }
  if (kind === "received") {
    return {
      kind: "received", siteKey, item, day,
      idem_key: idem(siteKey, "rcv", item, day, tapNonce()),
      activity: { action: "received", item },
    };
  }
  if (kind === "kitchen") {
    const map = KITCHEN_EVENT[a.action];
    if (!map) return { _error: "unmapped kitchen action " + a.action };
    const entry = {
      kind: "kitchen", siteKey, item, action: a.action, day,
      event: map.event, flag: map.flag ?? null, ordered: map.ordered || false,
      supplierRef: a.supplierRef ?? null, eta: a.eta ?? null, note: a.note ?? null,
      photoPath: a.photoPath ?? null, qty: a.qty ?? null,
      tappedAt: (() => { try { return new Date().toISOString(); } catch (e) { return null; } })(),
      idem_key: idem(siteKey, a.action, item, day),
      activity: { action: a.action, item },
    };
    // Lane BP (13 Sep 2026): the tablet's own GPS fix rides the entry ONLY when the
    // write-drop ON caller supplied one (postKitchenAction hands over lat/lng/accuracy
    // for a GEOFENCED action). The OFF-path recorder never does, so the builder ==
    // recorder equality (tested) and today's insert payload are untouched. The row
    // lands with device_lat/lng/accuracy_m and the database stamps offsite (1300).
    // Because the shadow enqueue and this awaited entry share one idem_key, the outbox
    // MERGES them ({...shadow, ...this}) — the fix survives the collapse (tested).
    if (a.deviceLat != null && a.deviceLng != null) {
      entry.deviceLat = a.deviceLat;
      entry.deviceLng = a.deviceLng;
      entry.deviceAccuracyM = a.deviceAccuracyM != null ? a.deviceAccuracyM : null;
    }
    return entry;
  }
  return { _error: "unknown kind " + kind };
}

// Enqueue durably, register a per-entry waiter, verify+stamp, drain, and resolve
// on THIS entry's own landing (or a timeout). Never throws.
async function durableRecordAwaited(label, { staffPin, siteKey }, entry, { timeoutMs = 8000 } = {}) {
  try {
    outbox.enqueue(entry);                                       // (1) durable FIRST
    const landing = outbox.awaitEntry(entry.idem_key, timeoutMs); // (2) waiter BEFORE the flush
    const pass = await ensurePass(staffPin, siteKey);            // (3) per-action attribution
    if (pass?.ok) {
      try { const cur = await currentPass(); if (cur?.staffId) outbox.stampStaff(entry.idem_key, cur.staffId); }
      catch (e) { /* stamp best-effort; an un-stamped row is refused by insertStockRow -> honest !ok */ }
    } else {
      console.warn(`[stock-sync] ${label} — no warm pass on the authoritative path; write stays queued, reporting !ok now`);
    }
    flushStockOutbox();                                          // (4) drive the drain (don't await the aggregate)
    const outcome = await landing;                               // ...resolve on THIS entry only
    // The raw outbox errMsg ("timeout"/"offline"/"not landed") is for logs; staff
    // always see the same plain-English retry line the Sheet path shows.
    return {
      ok: !!outcome.ok,
      errMsg: outcome.ok ? "" : "That didn't save — try again.",
      rawErr: outcome.ok ? "" : (outcome.errMsg || ""),
      queued: outbox.size() > 0,
      pass,
    };
  } catch (e) {
    console.warn(`[stock-sync] ${label} — authoritative record threw (write stays queued):`, e?.message || e);
    return { ok: false, errMsg: "That didn't save — try again.", queued: true, error: e };
  }
}

// The screens' entry point on the write-drop ON path. Returns {ok, errMsg, queued,
// pass}. `pass` is handed back so a kitchen caller can reuse the SAME login for the
// reorder signal (one round-trip per tap), mirroring durableRecord.
export async function stockWriteAuthoritative({ kind, args = {}, timeoutMs = 8000 } = {}) {
  const entry = buildStockEntry(kind, args);
  if (entry && entry._error) {
    console.warn("[stock-sync] authoritative write — " + entry._error);
    return { ok: false, errMsg: "That didn't save — try again.", queued: false };
  }
  return durableRecordAwaited(`authoritative ${kind} ${args.item ?? ""}`, { staffPin: args.staffPin ?? null, siteKey: args.siteKey }, entry, { timeoutMs });
}

// --- the kitchen doorbell (read-side, step 1 of the read flip) ----------------
// The kitchen subscribes to Supabase realtime and treats every stock event as a
// doorbell ring: "something changed — refetch the list now". The list CONTENT
// still comes from the sheet feed (it alone knows about Command Centre 'ordered'
// events until Phase 3), so this can't show wrong data — it only makes the
// existing data instant. While the doorbell is healthy the 5s poll relaxes to a
// slow safety net.
//
// Rings only need the tablet's anonymous session (0006 read policy) — no PIN.
// Gated to the kitchen host; `?readflip=on|off` overrides for testing.
const READ_FLIP_HOSTS = new Set(["mcr-kitchen.eatrolled.com"]);
export function readFlipEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("readflip");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return READ_FLIP_HOSTS.has((window.location.hostname || "").toLowerCase());
}

// Subscribe. onRing() fires on every stock_requests/stock_activity change (any
// site — the kitchen watches all sites anyway); onLive(bool) reports whether the
// subscription is currently healthy so callers can relax/restore their poll.
// Returns an unsubscribe function. Never throws.
export function subscribeDoorbell({ onRing, onLive } = {}) {
  if (!readFlipEnabled()) return () => {};
  let ch = null;
  let stopped = false;
  (async () => {
    try {
      await ensureAnonSession(); // realtime delivers nothing to a signed-out socket
      if (stopped) return;
      ch = supabase.channel("stock-doorbell")
        .on("postgres_changes", { event: "*", schema: "public", table: "stock_requests" }, () => { try { onRing && onRing(); } catch (e) {} })
        .on("postgres_changes", { event: "*", schema: "public", table: "stock_activity" }, () => { try { onRing && onRing(); } catch (e) {} })
        .subscribe((status) => {
          try { onLive && onLive(status === "SUBSCRIBED"); } catch (e) {}
        });
    } catch (e) {
      console.warn("[stock-sync] doorbell unavailable:", e?.message || e);
      try { onLive && onLive(false); } catch (e2) {}
    }
  })();
  return () => {
    stopped = true;
    try { if (ch) supabase.removeChannel(ch); } catch (e) {}
  };
}

// --- re-exports so screens import login + reads + realtime from one place -----
export { loginWithPin, currentPass, lock };
export { ensureAnonSession } from "./auth.js";
export { subscribeToStock, listOpenRequests, listRecentActivity, londonToday } from "./stock.js";
